import { useEffect, useState } from "react";
import type { MovieFilter } from "@/lib/types";

interface FilterOptions {
  type: string[];
  region: string[];
  genre: string[];
  year: string[];
  language: string[];
}

interface FilterBarProps {
  filter: MovieFilter;
  options: FilterOptions;
  activeCount: number;
  onChange: (key: keyof MovieFilter, value: string) => void;
  onReset: () => void;
}

const rows: { key: keyof MovieFilter; label: string; optionKey: keyof FilterOptions }[] = [
  { key: "type", label: "类型", optionKey: "type" },
  { key: "region", label: "地区", optionKey: "region" },
  { key: "genre", label: "题材", optionKey: "genre" },
  { key: "year", label: "年份", optionKey: "year" },
  { key: "language", label: "语言", optionKey: "language" },
];

export default function FilterBar({
  filter,
  options,
  activeCount,
  onChange,
  onReset,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  return (
    <>
      <div className="hidden md:block rounded-lg border border-background-200/70 bg-background-100/60 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/60">
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <i className="ri-filter-3-line text-accent-500"></i>
            组合筛选
            {activeCount > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-accent-500/15 text-accent-500 font-label">
                {activeCount}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onReset}
            disabled={activeCount === 0}
            className={`inline-flex items-center gap-1 text-xs transition-colors whitespace-nowrap ${
              activeCount === 0
                ? "text-foreground-800 cursor-not-allowed"
                : "text-foreground-400 hover:text-accent-500 cursor-pointer"
            }`}
          >
            <i className="ri-restart-line"></i>
            重置筛选
          </button>
        </div>

        <div className="divide-y divide-background-200/50">
          {rows.map((row) => (
            <div key={row.key} className="flex items-start gap-4 px-4 py-3">
              <span className="w-10 shrink-0 pt-1.5 text-xs text-foreground-600">{row.label}</span>
              <div className="flex flex-wrap gap-2 min-w-0">
                <FilterChip
                  label="全部"
                  active={filter[row.key] === "全部"}
                  onClick={() => onChange(row.key, "全部")}
                />
                {options[row.optionKey].map((option) => (
                  <FilterChip
                    key={option}
                    label={option}
                    active={filter[row.key] === option}
                    onClick={() => onChange(row.key, option)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:hidden flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex-1 h-11 rounded-md border border-background-300 bg-background-100 flex items-center justify-center gap-2 text-sm text-foreground-200 cursor-pointer"
        >
          <i className="ri-filter-3-line text-accent-500"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-accent-500/15 text-accent-500 text-[11px] font-label">
              {activeCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={onReset}
          aria-label="重置筛选"
          className="w-11 h-11 rounded-md border border-background-300 flex items-center justify-center text-foreground-400 cursor-pointer"
        >
          <i className="ri-restart-line"></i>
        </button>
      </div>

      {open && (
        <div className="md:hidden fixed inset-0 z-[60] flex">
          <button
            type="button"
            aria-label="关闭筛选面板"
            onClick={() => setOpen(false)}
            className="flex-1 bg-background-50/80 backdrop-blur-sm cursor-pointer"
          ></button>
          <div className="w-[86%] max-w-sm h-full bg-background-100 border-l border-background-200 flex flex-col animate-fade-in">
            <div className="flex items-center justify-between px-4 h-14 border-b border-background-200/70 shrink-0">
              <span className="font-heading text-base font-semibold text-foreground-50">
                筛选条件
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 -mr-1 rounded-md flex items-center justify-center text-foreground-400 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {rows.map((row) => (
                <div key={row.key} className="mb-6">
                  <p className="mb-2.5 text-xs text-foreground-600">{row.label}</p>
                  <div className="flex flex-wrap gap-2">
                    <FilterChip
                      label="全部"
                      active={filter[row.key] === "全部"}
                      onClick={() => onChange(row.key, "全部")}
                    />
                    {options[row.optionKey].map((option) => (
                      <FilterChip
                        key={option}
                        label={option}
                        active={filter[row.key] === option}
                        onClick={() => onChange(row.key, option)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="shrink-0 p-4 border-t border-background-200/70 flex gap-2">
              <button
                type="button"
                onClick={onReset}
                className="flex-1 h-11 rounded-md border border-background-300 text-sm text-foreground-300 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-foreground-50 text-sm font-medium cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FilterChip({ label, active, onClick }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
        active
          ? "bg-primary-500 text-foreground-50 font-medium"
          : "bg-background-200/60 text-foreground-400 hover:bg-background-300/70 hover:text-foreground-100"
      }`}
    >
      {label}
    </button>
  );
}